import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = import.meta.env.VITE_API_URL as string;
const FLUSH_INTERVAL = 10_000;
const MAX_BATCH = 15;

interface TrackEvent {
  name: string;
  data?: Record<string, string>;
  ts: number;
  sessionId: string;
}

function getSessionId(): string {
  let sid = sessionStorage.getItem('_bws_sid');
  if (!sid) {
    sid = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem('_bws_sid', sid);
  }
  return sid;
}

let pendingRetry: TrackEvent[] = [];
// Shared module-level buffer so ANY component (via track() / useTrackEvent) feeds
// the same queue that usePageTracker — mounted once in the authenticated Layout —
// batches and flushes.
let eventBuffer: TrackEvent[] = [];

/**
 * Log a discrete UI event from anywhere (components, event handlers). PII-free:
 * pass only action names + small metadata (never field values or image bytes).
 * Never throws — tracking must be invisible to the user.
 */
export function track(name: string, data?: Record<string, string>) {
  try {
    eventBuffer.push({ name, data, ts: Date.now(), sessionId: getSessionId() });
    if (eventBuffer.length >= MAX_BATCH) flushEvents(eventBuffer.splice(0));
  } catch { /* ignore */ }
}

async function flushEvents(events: TrackEvent[]) {
  if (events.length === 0) return;
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) {
      // Auth not ready yet — re-queue events for next flush
      pendingRetry.push(...events);
      return;
    }

    const body = JSON.stringify({ events });

    if (typeof navigator.sendBeacon === 'function') {
      // sendBeacon doesn't support custom headers, use fetch with keepalive instead
      fetch(`${API_URL}/behavior/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body,
        keepalive: true,
      }).catch(() => {});
    } else {
      fetch(`${API_URL}/behavior/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body,
      }).catch(() => {});
    }
  } catch {
    // Silently ignore — tracking must never fail visibly
  }
}

/**
 * Tracks page views automatically on route changes and provides trackEvent for explicit events.
 * Mount once inside the authenticated Layout component.
 */
// Hook wrapper for components that prefer a hook to the module function.
export function useTrackEvent() {
  return useCallback((name: string, data?: Record<string, string>) => track(name, data), []);
}

// ── Global click capture ─────────────────────────────────────────────────────
// A single delegated listener turns real interactions (button/link clicks) into
// `click` events, so the activity timeline shows what members DO — not just the
// pages they land on. No per-component wiring: annotate important controls with
// `data-track="clean_name"` for a readable label; everything else falls back to
// its accessible label. Opt a noisy control (or a whole subtree) out with
// `data-track="off"`.
const LABEL_MAX = 48;
const EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/g;

// PII guard: never let a recipient's address ride along inside a label.
function redact(s: string): string {
  return s.replace(EMAIL_RE, '(email)').replace(/\s+/g, ' ').trim().slice(0, LABEL_MAX);
}

// The element's "primary line" — a clean label instead of its whole concatenated
// subtree. Large clickable cards/options wrap several text nodes; textContent
// would glue them ("Badge NamemtdakIssuer…"). Prefer, in order: a heading-ish
// descendant (card titles), then the first child node that actually carries text
// (the primary line of an option row / icon+label button).
function primaryText(el: HTMLElement): string {
  const h = el.querySelector('h1,h2,h3,h4,h5,h6,[role="heading"]');
  if (h?.textContent?.trim()) return h.textContent;
  for (const n of Array.from(el.childNodes)) {
    const txt = (n.textContent || '').trim();
    if (txt) return txt;
  }
  return el.textContent || '';
}

// Derive a concise, PII-safe label for the interactive element under the click.
function clickLabel(start: Element): string | null {
  const el = start.closest('[data-track],button,a[href],[role="button"]') as HTMLElement | null;
  if (!el) return null;
  if (el.closest('[data-track="off"]')) return null;

  const explicit = el.getAttribute('data-track');
  if (explicit && explicit !== 'off') return explicit.slice(0, LABEL_MAX);

  const aria = el.getAttribute('aria-label') || el.getAttribute('title');
  if (aria && aria.trim()) return redact(aria);

  const text = primaryText(el).trim();
  if (text) return redact(text);

  // Icon-only link with no text/label — fall back to its in-app route (no ids/query).
  if (el.tagName === 'A') {
    const href = (el as HTMLAnchorElement).getAttribute('href') || '';
    if (href && href.startsWith('/')) return href.split('?')[0].slice(0, LABEL_MAX);
  }
  return null;
}

let lastClick = { label: '', ts: 0 };

/**
 * Auto-capture interactive clicks as `click` behavior events. Mounted by
 * usePageTracker (once, in Layout). Never throws — tracking stays invisible.
 */
export function useClickTracker() {
  useEffect(() => {
    const onClick = (ev: MouseEvent) => {
      try {
        const target = ev.target as Element | null;
        if (!target) return;
        const label = clickLabel(target);
        if (!label) return;
        const now = Date.now();
        // Swallow double-fires and rapid repeats of the same control.
        if (label === lastClick.label && now - lastClick.ts < 800) return;
        lastClick = { label, ts: now };
        track('click', { label, page: window.location.pathname });
      } catch { /* ignore */ }
    };
    // Capture phase so we still see clicks whose handlers stopPropagation().
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);
}

export function usePageTracker() {
  const location = useLocation();
  const prevPath = useRef<string>('');

  // Capture interactive clicks (buttons/links) alongside page views.
  useClickTracker();

  // Auto-track page views on route change
  useEffect(() => {
    const path = location.pathname;
    if (path === prevPath.current) return;
    prevPath.current = path;
    track('page_view', { page: path });
  }, [location.pathname]);

  // Periodic flush of the shared buffer
  useEffect(() => {
    const interval = setInterval(() => {
      const retry = pendingRetry.splice(0);
      if (retry.length > 0) eventBuffer.unshift(...retry);
      if (eventBuffer.length > 0) flushEvents(eventBuffer.splice(0));
    }, FLUSH_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  // Flush on page unload
  useEffect(() => {
    const handleUnload = () => {
      if (eventBuffer.length > 0) flushEvents(eventBuffer.splice(0));
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  // Track session start
  useEffect(() => {
    track('session_start');
  }, []);

  return { trackEvent: track };
}
